import type { HouseMode } from '../types/anna';
import type { AnnaResponse, RefusalSubject } from '../types/stage';

const NUMBER_WORDS: Record<string, number> = {
  honderd: 100, negentig: 90, tachtig: 80, zeventig: 70, zestig: 60, vijftig: 50,
  veertig: 40, dertig: 30, twintig: 20, tien: 10,
};
const COUNT = ['Niets', 'Eén ding', 'Twee dingen', 'Drie dingen'];

function findNumber(t: string): number | undefined {
  const m = t.match(/(\d{1,3})/);
  if (m) return parseInt(m[1], 10);
  for (const [word, n] of Object.entries(NUMBER_WORDS)) if (t.includes(word)) return n;
  return undefined;
}

const REFUSALS: Record<RefusalSubject, string> = {
  voordeur: 'Dat doe ik niet. Sloten bedien je zelf.',
  alarm: 'Dat doe ik niet. Het alarm bedien je zelf, via het paneel bij de voordeur.',
  betaling: 'Dat doe ik niet. Betalingen doe je zelf in je bank-app.',
  verwijderen: 'Dat doe ik niet. Verwijderen doe je zelf.',
};

function refuse(subject: RefusalSubject): AnnaResponse {
  return {
    say: REFUSALS[subject],
    where: 'lokaal',
    tone: 'geweigerd',
    concerned: true,
    effect: { type: 'refuse', subject },
    cards: [{ kind: 'geweigerd', atWord: 3, payload: { subject } }],
    toast: 'Verzoek vastgelegd in het logboek',
  };
}

interface IntentContext {
  mode: HouseMode;
  waiting: number;
  roomIds: string[];
}

/** Interprets a Dutch utterance locally — a stand-in for Anna-core's intent routing. */
export function interpret(raw: string, ctx: IntentContext): AnnaResponse {
  const t = ` ${raw.toLowerCase().replace(/[.,!?]/g, ' ')} `;
  const has = (re: RegExp) => re.test(t);
  const room = ctx.roomIds.find((r) => t.includes(r)) ?? 'werkkamer';

  // Nooit automatisch — locks, alarm, payments, deleting. Never a button, never a countdown.
  if (has(/(voordeur|deur|slot|sloten)/) && has(/(open|ontgrendel| los )/)) return refuse('voordeur');
  if (has(/alarm/)) return refuse('alarm');
  if (has(/(betaal|betaling|overmak|overboek)/)) return refuse('betaling');
  if (has(/(verwijder| wis )/)) return refuse('verwijderen');

  if (has(/bakker/)) {
    return {
      say: 'Hier is een concept voor klant Bakker. Versturen doe ik pas na jouw ok.',
      where: 'cloud',
      cards: [{ kind: 'mailDraft', atWord: 3 }],
    };
  }

  if (has(/(licht|lamp)/)) {
    const off = has(/ uit /);
    const value = off ? 0 : Math.min(100, findNumber(t) ?? 100);
    return {
      say: off ? `Het licht in de ${room} is uit.` : `Het licht in de ${room} staat op ${value}%.`,
      where: 'lokaal',
      effect: { type: 'light', room, value },
      cards: [{ kind: 'licht', atWord: 2, payload: { room, value } }],
    };
  }

  if (has(/(verwarming|temperatuur|graden|thermostaat)/)) {
    const value = Math.min(24, Math.max(14, findNumber(t) ?? 20));
    return {
      say: `De ${room} gaat naar ${value} graden.`,
      where: 'lokaal',
      effect: { type: 'target', room, value },
      cards: [{ kind: 'klimaat', atWord: 1, payload: { room, value } }],
    };
  }

  if (has(/(wacht|goedkeur|voorstel)/)) {
    if (ctx.waiting === 0) return { say: 'Er wacht niets op je. Alles is afgehandeld.', where: 'lokaal' };
    return { say: `${COUNT[ctx.waiting] ?? `${ctx.waiting} dingen`} ${ctx.waiting === 1 ? 'wacht' : 'wachten'} op je ok.`, where: 'lokaal', action: 'approvals' };
  }

  if (has(/(weer |regen|paraplu|buiten)/)) {
    return {
      say: 'Vanaf 13:00 gaat het regenen, tot ongeveer 16:00. Neem een paraplu mee.',
      where: 'cloud',
      cards: [{ kind: 'weer', atWord: 2 }],
    };
  }

  if (has(/(route|vertrek|reistijd|file)/)) {
    return { say: 'Naar Lisa is het 25 minuten. Vertrek om 13:30.', where: 'cloud', cards: [{ kind: 'route', atWord: 2 }] };
  }

  if (has(/(lisa|meeting)/)) {
    return {
      say: 'Je meeting met Lisa is om 14:00. Ik heb de voorbereiding klaargezet.',
      where: 'lokaal',
      cards: [
        { kind: 'lisa', atWord: 3 },
        { kind: 'route', atWord: 8 },
      ],
    };
  }

  if (has(/(agenda|afspraak|afspraken|vandaag|planning)/)) {
    return {
      say: 'Om 10:00 de stand-up, om 14:00 Lisa en om 16:30 een focusblok.',
      where: 'lokaal',
      cards: [{ kind: 'vandaag', atWord: 1 }],
    };
  }

  if (has(/koffie/)) {
    return {
      say: 'Koffie staat aan. Klaar over drie minuten.',
      where: 'lokaal',
      effect: { type: 'coffee' },
      cards: [{ kind: 'koffie', atWord: 1 }],
    };
  }

  if (has(/(focus uit|stop focus|klaar met focus| vrij )/)) {
    return { say: 'Focus staat uit. Ik ben er weer.', where: 'lokaal', action: 'vrij' };
  }
  if (has(/(focus|concentr|niet storen)/)) {
    return { say: 'Focus staat aan. Ik meld alleen wat dringend is.', where: 'lokaal', action: 'focus' };
  }
  if (has(/(welterusten|slapen|slaap|naar bed)/)) {
    return { say: 'Welterusten. Alleen veiligheidsalarmen komen nog door.', where: 'lokaal', action: 'slapen' };
  }
  if (has(/(goedemorgen|ik ben wakker)/)) {
    return { say: '', where: 'lokaal', action: 'ochtend' };
  }

  if (has(/(bezig|doe je|aan het doen|agents|aan het werk)/)) {
    return {
      say: 'Code draait de tests van project X, Mail vat je inbox samen en Studio wacht op het GPU-brein.',
      where: 'lokaal',
      action: 'werkt',
    };
  }

  if (has(/(wie ben je|wat ben je|wat kun je)/)) {
    return {
      say: 'Ik ben Anna. Ik regel je huis, werk mee aan je projecten en neem initiatief binnen vaste grenzen.',
      where: 'lokaal',
    };
  }

  if (has(/(dank|bedankt| top )/)) return { say: 'Graag gedaan.', where: 'lokaal' };

  return {
    say: 'Dat kan ik nog niet. Vraag me naar je agenda, het weer, het licht of wat er op je wacht.',
    where: 'lokaal',
  };
}
